import {
  WSTable,
  WSTableColumnSortDirection,
  WSTableHeaderAction,
  WSTableItem
} from "@wingspanhq/fe-component-library";
import React, { useMemo } from "react";
import {
  Payable,
  PayablesSortField
} from "../../../../services/api/payments/payables";
import { ColumnKey, getColumns, PayeeDisplayInfo } from "./columns";

export type Props = {
  payables: Payable[];
  onRowClick?: (payable: Payable) => void;
  onSortChange?: (
    field: PayablesSortField,
    direction: WSTableColumnSortDirection
  ) => void;
  sortField?: PayablesSortField;
  sortDirection?: WSTableColumnSortDirection;
  selection?: WSTableItem<Payable>[];
  onSelectionChange?: (selection: WSTableItem<Payable>[]) => void;
  visibleColumns?: Partial<Record<ColumnKey, boolean>>;
  // Used to look up payee display names; per-tab caller can pass a Map from a usePayeeRows query.
  // For now, fall back to "Payee" + truncated payeeId if no map.
  payeesMap?: Map<string, PayeeDisplayInfo>;
  // CSV download is rendered inside the table chrome (top-right header action),
  // mirroring legacy PayablesTable. Only shown when `canDownloadCsv` is true.
  canDownloadCsv?: boolean;
  isDownloadingCsv?: boolean;
  onDownloadCsv?: () => void;
};

export const TablePayables: React.FC<Props> = props => {
  const tableData = useMemo<WSTableItem<Payable>[]>(
    () =>
      props.payables.map(payable => ({
        id: payable.payableId ?? "",
        data: payable
      })),
    [props.payables]
  );

  const columns = useMemo(
    () =>
      getColumns({
        visibleColumns: props.visibleColumns,
        payeesMap: props.payeesMap,
        sortField: props.sortField,
        sortDirection: props.sortDirection,
        onSortChange: props.onSortChange
      }),
    [
      props.visibleColumns,
      props.payeesMap,
      props.sortField,
      props.sortDirection,
      props.onSortChange
    ]
  );

  const handleRowClick = props.onRowClick
    ? (item: WSTableItem<Payable>) => props.onRowClick!(item.data)
    : undefined;

  const headerAction = useMemo<WSTableHeaderAction | undefined>(() => {
    if (!props.canDownloadCsv || !props.onDownloadCsv) {
      return undefined;
    }
    return {
      icon: "download",
      onClick: props.onDownloadCsv,
      loading: props.isDownloadingCsv
    };
  }, [props.canDownloadCsv, props.onDownloadCsv, props.isDownloadingCsv]);

  return (
    <WSTable<Payable>
      columns={columns}
      tableData={tableData}
      onRowClick={handleRowClick}
      selection={props.selection}
      onSelectionChange={props.onSelectionChange}
      headerAction={headerAction}
    />
  );
};
